import {useSuspenseQuery} from "@tanstack/react-query";
import axios from "axios";
import React, {useState} from "react";
import {useParams} from "react-router-dom";
import {useGoogleMaps} from "../hooks/useGoogleMaps";
import {
    getRecipientStopInfo,
    RecipientStopInfo,
    RecipientTrackingPageParams,
} from "../model/rideInfo";
import {Map} from "./Map";
import {PackageInfoPanel} from "./PackageInfoPanel";
import {getTheme} from "./getTheme";

export function getMapClass(collapsed: boolean) {
    if (collapsed) {
        return "map mh30";
    }
    return "map mh50";
}

const finiteStatuses: RecipientStopInfo["status"][] = [
    "DELIVERED",
    "CANCELLED",
    "UNDELIVERED",
    "DELIVERED_TO_PUDO",
];

export function RecipientTrackingPage() {
    const {shareKey} = useParams<RecipientTrackingPageParams>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {data: stop} = useSuspenseQuery({
        queryKey: ["delivery-stop"],
        queryFn: () => getRecipientStopInfo(shareKey),
        refetchInterval: (query) => {
            if (isModalOpen) return 0;
            return query?.state.data && finiteStatuses.includes(query.state.data.status) ? 0 : 5000;
        },
        retry: (failureCount, error) => {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return false;
            }
            return failureCount < 3;
        },
    });
    const mapLoaded = useGoogleMaps();

    const hasFinished = finiteStatuses.includes(stop.status || "");

    const pinUrl = () => {
        switch (stop.status) {
            case "UNDELIVERED":
            case "CANCELLED":
                return `/${getTheme()}/pin-error.svg`;
            case "DELIVERED_TO_PUDO":
                switch (stop.pudo?.pudoType) {
                    case "LOCKER":
                        return `/${getTheme()}/pin-locker.svg`;
                    case "PARCEL_SHOP":
                    default:
                        return `/${getTheme()}/pin-parcel-shop.svg`;
                }
            default:
                switch (stop.kind) {
                    case "PUDO":
                        return `/${getTheme()}/pin-parcel-shop.svg`;
                    default:
                        return `/${getTheme()}/pin.svg`;
                }
        }
    };

    function getContent() {
        // to prevent layout shift if waiting for map load
        if (!mapLoaded) {
            return <div className={getMapClass(hasFinished)}></div>;
        }

        return (
            <Map
                pickup={stop.pickupLocation}
                destination={stop.pudo?.location ?? stop.destinationLocation}
                driver={stop.currentLocation}
                route={stop.route}
                destinationPinIcon={pinUrl()}
                carColor={stop.carInfo?.color}
                wrapperClassName={getMapClass(hasFinished)}
            />
        );
    }

    return (
        <div className="map-page">
            <div className="map-page-content">
                {getContent()}
                <PackageInfoPanel stop={stop} onModalToggle={(open) => setIsModalOpen(open)} />
            </div>
        </div>
    );
}
